import { ISearchParameters, ITableReport, Report } from "./TableInterfaces";
import { DropdownItem } from "./dropdownInterfaces";
import { OrderBook, OrderStatus, OrderStrategy } from "./orderInterfaces";

export interface IStepAddOrderFormValues {
  userIds: number[];
  orderSide: number;
  instrumentType: StepInstrumentType | null;
  instrumentId: string | null;
  fromPrice: number;
  toPrice: number;
  stepCount: number;
  volume: number;
  blockSize: number;
  navBubblePercent: number | null;
  optionOrderType: number | null;
  volumeCalculationType: number;
  autoSellFund?: boolean;
  orderStartegyStr?: string;
  schedule?: IStepOrderSchedule | null;
  startImmediately?: boolean;
}

export interface IStepOrderSchedule {
  startTime: string | null;
  stopTime: string | null;
}

export interface IStepFundSellSettings {
  aheadBlocks: number;
}

export interface IStepFundOrderTableReport extends ITableReport {
  name: string;
  instrumentName: string;
  instrumentIsin: string;
  volume: number;
  price: number;
  timeStr: string;
  auto: boolean;
  previcePurchacingPower: number;
  done: number | null;
  errorCount: number | null;
  stepOrderId: number | null;
  blockIndex: number | null;
}

export enum VolumeCalculationType {
  FixedAmount = 1,
  PortfolioPercent,
  AssetPercent,
  TargetPortfolioPercent,
  BuyingPowerPercent,
  FixedCapitalAmount,
}

export interface IStepOptionDropdownItem extends DropdownItem {
  fullName: string;
  type: StepInstrumentType;
  contractSize: number;
}

export enum StepInstrumentType {
  Option = 1,
  Fund = 2,
  Normal = 3,
  Future = 4,
}

export enum StepOrderTypes {
  Hunt,
  Competition = 5,
}

export enum StepOrderSide {
  Buy = 1,
  Sell = 2,
}

export interface IStepOrderTableReport extends ITableReport {
  orderSideStr: string;
  sahraName: string;
  operator: string;
  namad: string;
  optionType: string;
  portfoAmount: number;
  totalSize: number;
  firstStep: number;
  lastStep: number;
  stepCount: number;
  activeStep: number;
  filledVolume: number;
  pendingVolume: number;
  pendingOrderCount: number;
  blockSize: number;
  priority: string;
  navBubblePercent: string;
  margin: string;
  instrumentType: StepInstrumentType;
  instrument: string;
  baseInstrument: string;
  optionInstrument: string;
  getGraphStr: string;
  orderStatus: OrderStatus;
  isActive: boolean;
  pausePlayDisabled: boolean;
  pausePlayPending: boolean;
  /** Retry cleanup without treating the locally paused order as active. */
  pauseRetryRequired?: boolean;
  editDisabled: boolean;
  pausePlayWarning: string | null;
  deletePending: boolean;
  deleteWarning: string | null;
  editWarning?: string | null;
  editExclusionWarning?: string | null;
  isDeleted: boolean;
  /** Client-only start time of the request providing this row's button state. */
  pausePlayStatusRequestedAt?: number;
  stateChangeSource: "Automatic" | "Manual";
  userId: number;
  orderStartegy: OrderStrategy;
  shekarOrderId: number;
  scheduledStartTimeStr: string;
  scheduledStopTimeStr: string;
  nextScheduledAt: string | null;
  nextScheduledAction: "Start" | "Stop" | null;
  serverNow: string;
}

export type IStepOrderPausePlayStatus = Pick<
  IStepOrderTableReport,
  | "id"
  | "isActive"
  | "pausePlayDisabled"
  | "pausePlayPending"
  | "pauseRetryRequired"
  | "editDisabled"
  | "pausePlayWarning"
  | "deletePending"
  | "deleteWarning"
  | "editWarning"
  | "editExclusionWarning"
  | "isDeleted"
>;

export interface IStepOrderStateChangeLog {
  id: number;
  accountName: string;
  symbol: string;
  actor: string;
  fromState: string;
  toState: string;
  changedAt: string;
}

export interface IStepSubOrdersTableReport extends ITableReport {
  methodName: string;
  orderStrategy: OrderStrategy;
  shekarOrderId: number;
  sahraUserId: number;
  sahraName: string;
  operator: string;
  orderSideStr: string;
  namad: string;
  totalSize: number;
  filledVolume: number;
  orderSide: StepOrderSide;
  pendingBuy: number;
  buyPrice: number;
  maxPairPosition: number;
  blockSize: number;
  priority: string;
  margin: string;
  sellInstrument: string;
  buyInstrument: string;
  getGraphStr: string;
  orderStatus: OrderStatus;
  isActive: boolean;
}

export interface IStepTradeTableReport extends ITableReport {
  methodName: string;
  orderStrategy: OrderStrategy;
  sahraName: string;
  operator: string;
  namad: string;
  orderSideStr: string;
  price: number;
  orderSide: StepOrderSide;
  volume: number;
  getGraphStr: string;
  sahraUserId: number;
  instrumentType: number;
  instrument: string;
  baseInstrument: string;
  optionInstrument: string;
  activeStep: number;
}

export interface IGetStepOrderBookResponse {
  fullOrderBook: OrderBook;
  fundInfo: IStepOrderBookFundInfo;
  instrumentStatistic: IInstrumentStatistic;
}

export interface IStepOrderBookFundInfo {
  nav: string;
  purchaseBubble: string;
  sellBubble: string;
}

export interface IInstrumentStatistic {
  final: IStatsRow;
  trade: IStatsRow;
  margin?: number;
  status?: string;
  maxPricePermit?: number;
  minPricePermit?: number;
}

interface IStatsRow {
  price: number;
  amount: number;
  percent: number;
}

export interface PortfoInfo extends ITableReport {
  insName: string;
  price: string;
  amount: number;
}

export interface IGetPortfoTableResponse {
  sahrausername: string;
  infos: Report<PortfoInfo>;
}

export enum StepOptionOrderType {
  Offset = 1,
  Open = 2,
}

export interface IStepChanceSearchParams extends ISearchParameters {
  stepOrderId: number;
}

export interface IBrokerOrdersSearchParam extends ISearchParameters {
  stepOrderId?: number;
  shekarOrderId?: number;
  orderId?: number;
  upairId?: number;
  nlegId?: number;
}

export interface IEditStepOrderFormValues {
  instrumentId: string;
  fromPrice: number;
  toPrice: number;
  blockSize: string | null;
  stepCount: number;
  remainedVolume: string | null;
  volumeCalculationType?: VolumeCalculationType | null;
  volumeCalculationValue?: number | null;
  stepOrderId: number;
  stepOrderIds: number[] | null;
  applyToAll: boolean | null;
  direction: StepOrderSide;
  schedule?: IStepOrderSchedule | null;
  startImmediately?: boolean;
}

export interface IStepEditVolumeContext {
  stepOrderId: number;
  volumeCalculationType: VolumeCalculationType;
  volumeCalculationValue: number;
  targetTotalVolume: number;
  priorFilledVolume: number;
  cumulativeFilledVolume: number;
  cumulativeExcludedVolume?: number;
  remainingVolume: number;
}

export interface IStepEditVolumePreview {
  success: boolean;
  reason?: string | null;
  calculatedVolume?: number | null;
  targetTotalVolume?: number | null;
  remainingVolume?: number | null;
}

export interface IFailedStepOrderEditItem {
  stepOrderId: number;
  accountName?: string | null;
  reason: string;
}

export interface IStepTablesSearchParam extends ISearchParameters {
  sahraUserid?: number;
  instrumentId?: string;
  portfolioGroupId?: number;
}

export interface IStepBrokerOrdersReport extends ITableReport {
  buyInsName: string;
  sellInsName: string;
  insName: string;
  type: string;
  orderPrice: number;
  orderPriceStr: string;
  price: string;
  creationTime: string;
  updateTime: string;
  exeTime: string;
  exeStr: string;
  fill: number;
  total: number;
  pending: number;
  isActive: boolean;
  errorMsg: string;
}

export interface IStepCompetitionConfigFormValues {
  totalActiveOrder: number;
  activeOrderPP: number;
  totalAmount: number;
  insCode: string;
}

export interface IStepCompetitionConfigTableReport extends ITableReport {
  isDeletable: boolean;
  namad: string;
  totalActiveOrder: number;
  activeOrderPP: number;
  totalAmount: number;
  insCode: string;
}

export interface IStepCompetitionConfigSearchParam {
  instrumentId?: string;
}

export interface IStepAggregationTableReport extends ITableReport {
  namad: string;
  pendingVolume: number;
  pendingCount: number;
  instrumentId: string;
  type: StepInstrumentType;
}

export interface IStepCompetitionChanceSearchParam extends ISearchParameters {
  stepOrderId: number;
}

export interface IStepOrderPreviewRow {
  userId: number;
  userName: string;
  orderSide: StepOrderSide;
  instrumentId: string;
  instrumentName: string;
  instrumentType: StepInstrumentType;
  firstStepPrice: number;
  lastStepPrice: number;
  stepCount: number;
  orderStartegyStr?: string;
  blockSize: number;
  volumeCalcMethod: string;
  originalVolumeInput: string;
  totalVolume: number;
  engagedCapitalAmount: number;
  autoSellFund: boolean;
  startTime?: string | null;
  stopTime?: string | null;
  optionOrderType?: StepOptionOrderType | null;
  previewStatus?: "Offline" | "Timeout" | "BrokenOrder" | "IncompleteNav" | "NoConnectedUser" | "Deduplicated" | null;
  previewMessage?: string | null;
}

export interface IStepOrderPreviewOrderDto {
  userIds: number[];
  orderSide: StepOrderSide;
  optionOrderType: number | null;
  optionOrderTypeEnum: unknown;
  instrumentType: StepInstrumentType;
  instrumentId: string;
  fromPrice: number;
  toPrice: number;
  stepCount: number;
  volume: number;
  blockSize: number;
  navBubblePercent: number | null;
  maxConcurrentOrder: number | null;
  volumeCalculationType: number;
  volumeCalculationTypeEnum: VolumeCalculationType;
  volumePerUser: Record<string, number>;
  autoSellFund?: boolean;
  schedule?: IStepOrderSchedule | null;
  startImmediately?: boolean;
}

export interface IOrderVolumeSummaryItem {
  orderSide: StepOrderSide;
  instrumentId: string;
  instrumentName: string;
  totalVolume: number;
  engagedCapitalAmount: number;
  totalActiveOrder?: number | null;
  activeOrderPP?: number | null;
  totalAmount?: number | null;
}

export interface IPreviewWarningRow {
  row: number;
  userName: string;
  message: string;
}

export interface IOrderFileUploadResponse<T extends ITableReport> {
  report: Report<T>;
  volumeSummary: IOrderVolumeSummaryItem[];
  warnings: IPreviewWarningRow[];
}

export interface IStepOrderPreview {
  rows: IStepOrderPreviewRow[];
  orderDto: IStepOrderPreviewOrderDto;
  timedOutUserIds?: number[];
  brokenOrderUserIds?: number[];
  incompleteNavUserIds?: number[];
  previewWarnings?: string[];
  volumeSummary?: IOrderVolumeSummaryItem[];
}
